// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a machine's settings that live in Konvo, on its page in Settings → Connections → Agents
// (design/agents-team-threads m6-machine-sharing; JP 2026-10-15: nothing to run on the machine).
// Its owner turns "Allow and Deny from Konvo" on or off (for them and teammates alike, in the app and
// on the lock screen; off for a new machine) and picks how far its threads may be shared (Prompt for a
// new machine), sees what is shared now, and can stop all sharing.

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {ChoiceRow, SettingsRow, SwitchRow} from '@app/features/konvo_agents/components/AgentSettingsRows';
import styles from '@app/features/konvo_agents/components/AgentShare.module.css';
import {canName, Face, levelName, shareErrorMessage} from '@app/features/konvo_agents/components/AgentShareBits';
import AgentShares from '@app/features/konvo_agents/state/AgentShares';
import Navigation from '@app/features/navigation/state/Navigation';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {
	AGENT_MACHINE_SHARING,
	type AgentMachineSharing as SharingLevel,
} from '@fluxer/schema/src/domains/konvo/AgentShares';
import type {AgentMachineView} from '@fluxer/schema/src/domains/konvo/Agents';
import {plural} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {IconShare, IconShieldCheck, IconUsers} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useState} from 'react';

export const AgentMachineSharing = observer(({machine}: {machine: AgentMachineView}) => {
	const {i18n, t} = useLingui();
	const [open, setOpen] = useState(false);
	const [saving, setSaving] = useState(false);
	useEffect(() => {
		void AgentShares.loadOwner();
	}, []);
	const cap: SharingLevel = machine.limits.share ?? 'off';
	const approve = machine.limits.approve;
	const mine = AgentShares.owner?.machines.find((entry) => entry.machineId === machine.id);
	const threads = mine?.threads ?? [];
	const people = new Map<string, string>();
	for (const thread of threads) for (const person of thread.people) people.set(person.userId, person.name);
	const machineName = machine.name;

	const save = async (patch: {share?: SharingLevel}) => {
		setSaving(true);
		try {
			await AgentShares.saveMachineSettings(machine.id, patch);
		} catch (error) {
			ToastCommands.error(shareErrorMessage(i18n, error));
		} finally {
			setSaving(false);
		}
	};
	const pickCap = (next: SharingLevel) => {
		if (next === cap) return;
		if (next !== 'off' || threads.length === 0) {
			void save({share: next});
			return;
		}
		ModalCommands.push(
			ModalCommands.modal(() => (
				<ConfirmModal
					title={t({
						message: `Turn team sharing off for ${machineName}?`,
						comment: 'Agents Settings: confirm switching team sharing off while threads are shared.',
					})}
					description={t({
						message:
							'Everyone you shared a thread with loses it at once. Their comments stay with the threads for you.',
						comment: 'Agents Settings: what switching team sharing off does.',
					})}
					primaryText={t({message: 'Turn off', comment: 'Agents Settings: switch team sharing off.'})}
					primaryVariant="danger"
					onPrimary={() => save({share: 'off'})}
				/>
			)),
		);
	};

	const stopAll = () => {
		ModalCommands.push(
			ModalCommands.modal(() => (
				<ConfirmModal
					title={t({
						message: `Stop sharing ${machineName}'s threads?`,
						comment: 'Agents Settings: confirm ending every team share of a machine’s threads.',
					})}
					description={t({
						message:
							'Everyone you shared a thread with loses it at once. Their comments stay with the threads for you. Team sharing itself stays on.',
						comment: 'Agents Settings: what Stop all sharing does.',
					})}
					primaryText={t({message: 'Stop all sharing', comment: 'Agents Settings: end every team share of a machine.'})}
					primaryVariant="danger"
					onPrimary={async () => {
						try {
							await AgentShares.stopAll(machine.id);
							ToastCommands.success(
								t({message: 'Sharing stopped.', comment: 'Agents Settings: toast after Stop all sharing.'}),
							);
						} catch (error) {
							ToastCommands.error(shareErrorMessage(i18n, error));
						}
					}}
				/>
			)),
		);
	};

	const sharedCount = threads.length;
	const levelHint = (level: SharingLevel) =>
		level === 'off'
			? t({message: 'Nobody else sees its chats', comment: 'Agents Settings: what team sharing Off means.'})
			: level === 'watch'
				? t({message: 'Read a shared chat live', comment: 'Agents Settings: what team sharing Watch allows.'})
				: level === 'comment'
					? t({message: 'Read and comment', comment: 'Agents Settings: what team sharing Comment allows.'})
					: approve
						? t({
								message: 'Send prompts, and Allow or Deny commands',
								comment: 'Agents Settings: what team sharing Prompt allows while Approve commands is on.',
							})
						: t({message: 'Send prompts too', comment: 'Agents Settings: what team sharing Prompt allows.'});
	return (
		<>
			<ChoiceRow
				icon={<IconUsers size={18} stroke={1.7} />}
				title={t({message: 'Sharing', comment: 'Agents Settings: row about sharing a machine’s chats with teammates.'})}
				description={t({
					message: 'The most teammates can do',
					comment: 'Agents Settings: the sharing level is the most a teammate can do in a chat shared from its header.',
				})}
				value={
					cap === 'off'
						? t({message: 'Off', comment: 'Agents Settings: team sharing switched off.'})
						: levelName(i18n, cap)
				}
				disabled={saving}
				groups={(onClose) => [
					{
						items: AGENT_MACHINE_SHARING.map((level) => ({
							label:
								level === 'off'
									? t({message: 'Off', comment: 'Agents Settings: team sharing switched off.'})
									: levelName(i18n, level),
							subtext: levelHint(level),
							selected: cap === level,
							onSelect: () => {
								onClose();
								pickCap(level);
							},
						})),
					},
				]}
				data-flx="konvo-agents.machine.sharing.cap"
			/>
			{(cap !== 'off' || sharedCount > 0) && (
				<div data-flx="konvo-agents.machine.sharing.shared">
					<SettingsRow
						icon={<IconShare size={18} stroke={1.7} />}
						title={t({message: 'Shared now', comment: 'Agents Settings: what is shared with teammates right now.'})}
						description={t({message: 'Chats shared from here', comment: 'Agents Settings: under Shared now.'})}
						trailing={
							<span className={styles.settingsSummary} data-flx="konvo-agents.machine.sharing.now">
								{people.size > 0 && (
									<span className={styles.faces}>
										{[...people.entries()].slice(0, 4).map(([userId, name]) => (
											<Face key={userId} userId={userId} name={name} size={24} />
										))}
									</span>
								)}
								{sharedCount === 0
									? t({message: 'None', comment: 'Agents Settings: no chat of this machine is shared.'})
									: plural(sharedCount, {one: '# chat', other: '# chats'})}
							</span>
						}
						onClick={sharedCount > 0 ? () => setOpen(!open) : undefined}
						aria-expanded={sharedCount > 0 ? open : undefined}
						data-flx="konvo-agents.machine.sharing.manage"
					/>
					{open && sharedCount > 0 && (
						<div className={styles.settingsOpen}>
							<ul className={styles.settingsList} data-flx="konvo-agents.machine.sharing.list">
								{threads.map((thread) => (
									<li key={thread.threadId}>
										<button
											type="button"
											className={styles.settingsThread}
											onClick={() => Navigation.navigateToAgents(machine.id, thread.threadId)}
										>
											{thread.title || thread.threadId}
										</button>
										<span className={styles.settingsPeople}>
											{thread.people.map((person) => `${person.name} (${canName(i18n, person.level)})`).join(', ')}
										</span>
									</li>
								))}
							</ul>
							<button
								type="button"
								className={clsx(styles.secondary, styles.danger)}
								onClick={stopAll}
								data-flx="konvo-agents.machine.sharing.stop"
							>
								<Trans comment="Agents Settings: end every team share of this machine's threads.">
									Stop all sharing
								</Trans>
							</button>
						</div>
					)}
				</div>
			)}
		</>
	);
});

/** Whether Allow and Deny in Konvo answer this machine's approval requests. */
export const AgentMachineApprove = observer(({machine}: {machine: AgentMachineView}) => {
	const {i18n, t} = useLingui();
	const [saving, setSaving] = useState(false);
	const save = async (approve: boolean) => {
		setSaving(true);
		try {
			await AgentShares.saveMachineSettings(machine.id, {approve});
		} catch (error) {
			ToastCommands.error(shareErrorMessage(i18n, error));
		} finally {
			setSaving(false);
		}
	};
	return (
		<SwitchRow
			icon={<IconShieldCheck size={18} stroke={1.7} />}
			title={t({
				message: 'Approve commands',
				comment: 'Agents Settings: switch that lets Konvo answer the machine’s approval requests.',
			})}
			description={t({
				message: 'Allow or deny from Konvo',
				comment:
					'Agents Settings: what the Approve commands switch means: in Konvo, on the lock screen and by someone a chat is shared with at Prompt.',
			})}
			value={machine.limits.approve}
			disabled={saving}
			onChange={(value) => void save(value)}
			data-flx="konvo-agents.machine.approve"
		/>
	);
});
